import { apiRequest } from './api.js';
import API_CONFIG from './api.js';

// Session management API calls - Pure API-based (no localStorage)
export const sessionService = {  // Get all sessions for the current user
  getSessions: async (token) => {
    const url = `${API_CONFIG.BACKEND_URL}/sessions`;
    
    try {
      const response = await apiRequest(url, {
        method: 'GET',
        token,
        headers: {
          'Accept-Language': 'en-US,en;q=0.9'
        }
      });
      
      // Transform API response to match current format
      if (response.sessions) {
        return response.sessions.map(session => ({
          id: session.session_id,
          title: session.session_name || 'New Conversation',
          messages: [], // Will be loaded separately when needed
          createdBy: '', // Could be populated from user info
          createdAt: session.created_at || session.last_modified || new Date().toISOString(),
          lastModified: session.last_modified || null, // Don't set to current time if not provided
          isFromAPI: true // Mark as API-sourced
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching sessions:', error);
      
      // Handle timeout errors specifically
      if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
        console.warn('Session fetch request timed out after 90 seconds');
        throw new Error('Request timeout exceeded (90 seconds). Please check your connection and try again.');
      }
      
      // Return empty array instead of throwing for other errors to allow graceful fallback
      return [];
    }
  },

  // Get detailed session with full conversation history
  getSessionDetails: async (sessionId, token) => {
    const url = `${API_CONFIG.BACKEND_URL}/sessions/${sessionId}`;
    
    try {
      const response = await apiRequest(url, {
        method: 'GET',
        token,
        headers: {
          'Accept-Language': 'en-US,en;q=0.9'
        }
      });
      
      // Transform questions object to messages array
      const messages = [];
      
      if (response.questions) {
        Object.entries(response.questions).forEach(([questionId, questionData]) => {
          // Add user message
          messages.push({
            id: `user-${questionId}-${Date.now()}`,
            sender: 'user',
            text: questionData.query,
            timestamp: new Date().toISOString(),
            question_id: parseInt(questionId)
          });
            // Add AI response
          const aiMessage = {
            id: `ai-${questionId}-${Date.now()}`,
            sender: 'ai',
            text: questionData.textual_response,
            tableData: questionData.results,
            sqlQuery: questionData.sql_query,
            contentType: questionData.content_type,
            question_id: parseInt(questionId),
            session_id: response.session_id,
            timestamp: new Date().toISOString(),
            isLoading: false,
            hasError: false
          };

          // Set message type based on whether there are table results
          if (questionData.results && Array.isArray(questionData.results) && questionData.results.length > 0) {
            aiMessage.type = 'table_response';
          }

          messages.push(aiMessage);
        });
      }
      
      return {
        id: response.session_id,
        title: response.session_name || 'New Conversation',
        messages: messages.sort((a, b) => a.question_id - b.question_id),
        createdBy: '',
        createdAt: response.created_at || response.last_modified || new Date().toISOString(),
        lastModified: response.last_modified || null,
        isFromAPI: true,
        sessionData: response // Keep original for reference
      };    } catch (error) {
      console.error(`Error fetching session ${sessionId}:`, error);
      
      // Handle timeout errors specifically
      if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
        console.warn(`Session ${sessionId} fetch request timed out after 90 seconds`);
        throw new Error(`Session fetch timeout exceeded (90 seconds). Please check your connection and try again.`);
      }
      
      // Check if it's a 500 error specifically
      if (error.message.includes('500') || 
          error.message.includes('Internal Server Error') || 
          error.message.includes('Internal server error')) {
        console.warn(`Session ${sessionId} returned 500 error - session may be corrupted`);
        throw new Error(`SESSION_ERROR: ${error.message}`);
      }
      
      return null;
    }
  },

  // Create a new session (if backend supports it)
  createSession: async (title, token) => {
    const url = `${API_CONFIG.BACKEND_URL}/sessions`;
    
    const requestBody = {
      session_name: title || 'New Conversation'
    };
    
    try {
      return await apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        token,
        headers: {
          'Accept-Language': 'en-US,en;q=0.9'
        }
      });    } catch (error) {
      console.error('Error creating session:', error);
      
      // Handle timeout errors specifically
      if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
        console.warn('Session creation request timed out after 90 seconds');
        throw new Error('Session creation timeout exceeded (90 seconds). Please check your connection and try again.');
      }
      
      throw error;
    }
  },

  // Update session title (if backend supports it)
  updateSession: async (sessionId, updates, token) => {
    const url = `${API_CONFIG.BACKEND_URL}/sessions/${sessionId}`;
    
    try {
      return await apiRequest(url, {
        method: 'PUT',
        body: JSON.stringify(updates),
        token,
        headers: {
          'Accept-Language': 'en-US,en;q=0.9'
        }
      });    } catch (error) {
      console.error(`Error updating session ${sessionId}:`, error);
      
      // Handle timeout errors specifically
      if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
        console.warn(`Session ${sessionId} update request timed out after 90 seconds`);
        throw new Error('Session update timeout exceeded (90 seconds). Please check your connection and try again.');
      }
      
      throw error;
    }
  },

  // Delete a session (if backend supports it)
  deleteSession: async (sessionId, token) => {
    const url = `${API_CONFIG.BACKEND_URL}/sessions/${sessionId}`;
    
    try {
      return await apiRequest(url, {
        method: 'DELETE',
        token,
        headers: {
          'Accept-Language': 'en-US,en;q=0.9'
        }
      });    } catch (error) {
      console.error(`Error deleting session ${sessionId}:`, error);
      
      // Handle timeout errors specifically
      if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
        console.warn(`Session ${sessionId} delete request timed out after 90 seconds`);
        throw new Error('Session deletion timeout exceeded (90 seconds). Please check your connection and try again.');
      }
      
      throw error;
    }
  }
};

export default sessionService;
